import { DateTime, Interval } from 'luxon'
import { useToast } from 'native-base'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import client from '../apolloClient'
import { ToastAlert } from '../components/Instructions/Common/ToastAlert'
import type { ProofOfServicePhoto } from '../components/Map/OrderMap'
import { type Address, type Order } from '@consumer/graphql'
import { AuthContext } from './AuthContext'
import { AppContext, CHECK_SIGN_IN_INTERVAL, defaultOrderState, INTERVAL_FROM_SIGN_IN } from './AppContext'
import { filterPII } from '@consumer/shared'

/**
 * Address fields that are visible only to authenticated users.
 * These are not PII (personally identifiable information) in the strict sense,
 * but they represent sensitive delivery preferences that unauthenticated visitors
 * should not see. Cleared immediately on auto-logout alongside the PII fields.
 */
const INSTRUCTION_FIELDS = new Set([
  'preferredLocation',
  'preferredPickupLocation',
  'securityCode',
  'type',
  'attributes',
  'instructions',
  'pickupInstructions',
])

/** Provides global app state — order data, sign-in status, and proof-of-service photos — to the tree. */
function AppContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const toast = useToast()
  const { firebaseUser, logout } = useContext(AuthContext)

  // Helper to filter sensitive address fields based on auth state
  const filterSensitiveAddressFields = useCallback(
    (address: Partial<Address>) => {
      if (!firebaseUser) {
        // Strip PII and instruction-related fields when the user is not authenticated
        return filterPII(address, INSTRUCTION_FIELDS)
      }
      return address // Return full address for authenticated users
    },
    [firebaseUser]
  )

  const [order, setOrder] = useState(defaultOrderState)
  const [proofOfServicePhotos, setProofOfServicePhotos] = useState<ProofOfServicePhoto[]>([])

  // Update handleSetOrder to filter sensitive fields
  const handleSetOrder = useCallback(
    (orderInput: Order) => {
      setOrder(() => ({
        ...orderInput,
        address: filterSensitiveAddressFields(orderInput?.address),
      }))
    },
    [filterSensitiveAddressFields]
  )

  const handleClearOrder = useCallback(() => {
    setOrder(defaultOrderState)
    setProofOfServicePhotos([])
  }, [setProofOfServicePhotos])

  const handleSetProofOfServicePhotos = useCallback(
    (proofOfServicePhotoInput: ProofOfServicePhoto[]) => {
      setProofOfServicePhotos(proofOfServicePhotoInput)
    },
    [setProofOfServicePhotos]
  )

  const checkSignSession = useCallback(() => {
    if (firebaseUser) {
      const lastSignInTime = firebaseUser.metadata.lastSignInTime
      if (!lastSignInTime) {
        logout()
        return
      }
      const timeZone = order.packages[0]?.timeZone ?? order?.timeZone ?? DateTime.now().zoneName
      const signInDateTime = DateTime.fromJSDate(new Date(lastSignInTime)).setZone(timeZone)
      if (signInDateTime) {
        const signInExpiration = DateTime.fromJSDate(new Date(lastSignInTime))
          .setZone(timeZone)
          .plus(INTERVAL_FROM_SIGN_IN)
        const interval = Interval.fromDateTimes(signInDateTime, signInExpiration)
        const now = DateTime.now().setZone(timeZone)
        if (!interval.contains(now)) {
          logout()
          client.refetchQueries({ include: 'all' })
          toast.show({
            placement: 'top',
            render: ({ id }) => (
              <ToastAlert
                id={id}
                title={'Logged out'}
                description={'You were logged out for security reasons'}
                status={'warning'}
                variant={'subtle'}
                isClosable={false}
                closeToast={toast.close}
              />
            ),
          })
        }
      }
    }
  }, [order, firebaseUser, logout, toast])

  useEffect(() => {
    const intervalId = setInterval(() => {
      checkSignSession()
    }, CHECK_SIGN_IN_INTERVAL)

    return () => clearInterval(intervalId)
  }, [checkSignSession])

  // Immediately strip instruction fields from the current order when the user
  // logs out. Without this, the fields remain visible until the Apollo refetch
  // triggered by checkSignSession completes its network round-trip.
  useEffect(() => {
    if (!firebaseUser) {
      setOrder(prev => ({
        ...prev,
        address: filterSensitiveAddressFields(prev.address as Partial<Address>),
      }))
    }
  }, [firebaseUser, filterSensitiveAddressFields])

  useEffect(() => {
    const handlePopState = () => {
      if (location?.pathname === '/') {
        handleClearOrder()
      }
    }
    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
  }, [handleClearOrder, location?.pathname])

  const value = useMemo(
    () => ({
      order,
      setOrder: handleSetOrder,
      clearOrder: handleClearOrder,
      proofOfServicePhotos,
      handleSetProofOfServicePhotos,
    }),
    [order, handleSetOrder, handleClearOrder, proofOfServicePhotos, handleSetProofOfServicePhotos]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export default AppContextProvider
